import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useI18nContext } from '@/i18n/I18n';

import { useUserProfile } from './useUserProfile';

const useUpgradeUnifiedAccount = (pageName: 'apiManagement') => {
  const { isUta: curAccountIsUta, updateUserProfile } = useUserProfile();
  const [search] = useSearchParams();
  const subAccountId = search.get('subaccount_id');
  const subAccountMemberTag = search.get('member_tag') || '';
  const [$t] = useI18nContext();
  const updateStatus = useRef<'success' | 'process' | ''>('');

  const isUta = useMemo(() => {
    if (pageName === 'apiManagement') {
      return subAccountId
        ? ['uta', 'uta2', 'utaProcess'].some((tag) =>
            subAccountMemberTag.includes(tag),
          )
        : curAccountIsUta;
    }
    return true;
  }, [pageName, curAccountIsUta, subAccountMemberTag, subAccountId]);

  useEffect(() => {
    if (!isUta) {
      window.BybitUniFrame?.ready?.(
        ({ upgradeUnifiedAccount }: { upgradeUnifiedAccount: any }) => {
          const {
            USER_TYPE,
            MODAL_VISIBLE,
            THEME,
            UPGRADE_STATUS,
            // ACCOUNT_TYPE,
            // VIEW_ACCOUNT_TYPE,
            // TRADE_TYPE,
            // PAGE_NAME,
          } = upgradeUnifiedAccount.properties;
          upgradeUnifiedAccount.init().then(() => {
            upgradeUnifiedAccount
              .setProperty(USER_TYPE, '0')
              .setProperty(MODAL_VISIBLE, false)
              .setProperty(UPGRADE_STATUS, '0');
          });
        },
      );
    }
  }, [isUta]);

  const closeUpgradeUnifiedAccount = () => {
    window.BybitUniFrame?.ready?.(({ upgradeUnifiedAccount }: any) => {
      const { MODAL_VISIBLE } = upgradeUnifiedAccount.properties;
      upgradeUnifiedAccount.setProperty(MODAL_VISIBLE, false);
    });
  };

  const toggleUpgradeUnifiedAccount = (
    visible: boolean,
    callback: {
      success?: () => void;
      close?: (status: 'success' | 'process' | '') => void;
      upgrading?: () => void;
      fail?: () => void;
    },
  ) => {
    window.BybitUniFrame?.ready?.(({ upgradeUnifiedAccount }: any) => {
      const { MODAL_VISIBLE, THEME, TITLE } = upgradeUnifiedAccount.properties;
      const { UPGRADING_CALLBACK, SUCCESS_CALLBACK, FAIL_CALLBACK, CLOSE } =
        upgradeUnifiedAccount.events;

      const title = `${$t('upgradeUnifyAccount')} (${$t(
        'notUpgradedCannotCreateNewApi',
      )})`;

      const upgradingCallback = () => {
        updateStatus.current = 'process';
      };

      const successCallback = () => {
        updateStatus.current = 'success';
      };

      const failCallback = () => {
        callback.fail?.();
        updateStatus.current = '';
      };

      const closeCallback = () => {
        window.BybitUniFrame.ready(({ upgradeUnifiedAccount }: any) => {
          const { UPGRADING_CALLBACK, SUCCESS_CALLBACK, FAIL_CALLBACK, CLOSE } =
            upgradeUnifiedAccount.events;
          upgradeUnifiedAccount
            .unsubscribeEvent(UPGRADING_CALLBACK, upgradingCallback)
            .unsubscribeEvent(SUCCESS_CALLBACK, successCallback)
            .unsubscribeEvent(FAIL_CALLBACK, failCallback)
            .unsubscribeEvent(CLOSE, closeCallback);
        });
        closeUpgradeUnifiedAccount();
        callback.close?.(updateStatus.current);
      };

      upgradeUnifiedAccount
        .subscribeEvent(UPGRADING_CALLBACK, upgradingCallback)
        .subscribeEvent(SUCCESS_CALLBACK, successCallback)
        .subscribeEvent(FAIL_CALLBACK, failCallback)
        .subscribeEvent(CLOSE, closeCallback)
        .setProperty(TITLE, title)
        .setProperty(THEME, 'theme-light')
        .setProperty(MODAL_VISIBLE, visible);
    });
  };

  useEffect(() => {
    return () => {
      updateUserProfile();
      closeUpgradeUnifiedAccount();
    };
  }, []);

  return {
    toggleUpgradeUnifiedAccount,
    needShowUpgradeUnifiedAccount: !isUta,
  };
};

export default useUpgradeUnifiedAccount;
